import { supabase } from '../supabase';

/* ============================================
   CONSTANTS
   ============================================ */

export const ROLE_OPTIONS = [
  'Engineer',
  'Product Manager',
  'Designer',
  'Founder',
  'Manager',
  'Student',
  'Other',
];

// Per spec: cosmetic position skip — 17 spots per referral.
// 3 referrals = -51 spots, 5 = -85, 10 = -170. Pure UI device, DB unchanged.
export const SPOTS_PER_REFERRAL = 17;
export const REFERRAL_GOAL = 3;

// Cold-start banner copy: don't show "Join 0 thinkers waiting".
export const BANNER_COUNT_THRESHOLD = 100;

const STATS_CACHE_KEY = 'shastraai_waitlist_stats_cache';
const STATS_CACHE_MS = 60_000;

/* ============================================
   API CALLS
   ============================================ */

export async function submitWaitlist({ email, name, role, biggest_challenge, referral_code }) {
  const response = await fetch('/.netlify/functions/waitlist-signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: String(email || '').trim().toLowerCase(),
      name: String(name || '').trim(),
      role: String(role || '').trim(),
      biggest_challenge: biggest_challenge ? String(biggest_challenge).trim().slice(0, 200) : null,
      referral_code: referral_code ? String(referral_code).trim().toUpperCase() : null,
    }),
  });
  let payload;
  try { payload = await response.json(); }
  catch (_) { throw new Error('Server returned an invalid response'); }
  if (!response.ok) throw new Error(payload?.error || `Signup failed (${response.status})`);
  return payload;
}

// Cached client-side too — banner gets remounted as user navigates.
export async function getWaitlistStats() {
  // Try sessionStorage first
  try {
    const cached = sessionStorage.getItem(STATS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.ts < STATS_CACHE_MS) return parsed.data;
    }
  } catch (_) { /* private mode etc. — skip cache */ }

  const r = await fetch('/.netlify/functions/waitlist-stats');
  if (!r.ok) throw new Error(`Stats fetch failed (${r.status})`);
  const data = await r.json();
  try {
    sessionStorage.setItem(STATS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch (_) { /* skip */ }
  return data;
}

// User's status by their referral code (called from confirmation page)
export async function getUserStatusByCode(code) {
  if (!code) return null;
  const { data, error } = await supabase.rpc('get_user_status_by_code', { code });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    // Note: SQL renamed `position` -> `user_position` (PostgreSQL reserved word).
    position: row.user_position ?? null,
    referrals_count: Number(row.referrals_count) || 0,
    name: row.name || null,
  };
}

// Polled lightweight count (every ~10s on confirmation page)
export async function getReferralsCountByCode(code) {
  if (!code) return 0;
  const { data, error } = await supabase.rpc('get_referrals_count_by_code', { code });
  if (error) throw error;
  return Number(data) || 0;
}

/* ============================================
   POSITION MATH (cosmetic skip)
   ============================================ */

export function effectivePosition(rawPosition, referralsCount) {
  if (rawPosition == null) return null;
  const skipped = (referralsCount || 0) * SPOTS_PER_REFERRAL;
  return Math.max(1, rawPosition - skipped);
}

export function spotsSkipped(referralsCount) {
  return (referralsCount || 0) * SPOTS_PER_REFERRAL;
}

/* ============================================
   SHARE LINKS
   ============================================ */

export function getShareUrl(referralCode) {
  if (typeof window === 'undefined') return `https://curx.ai/waitlist?ref=${referralCode}`;
  const origin = window.location.origin;
  return `${origin}/waitlist?ref=${referralCode}`;
}

export function generateShareLinks(referralCode) {
  const url = getShareUrl(referralCode);
  const text = encodeURIComponent(
    `I just joined the Curx AI waitlist — AI work intelligence for thinkers. Reserve your spot:`
  );
  const encodedUrl = encodeURIComponent(url);
  return {
    url,
    twitter:  `https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    email:    `mailto:?subject=${encodeURIComponent("Thought you'd like Curx AI")}&body=${text}%20${encodedUrl}`,
  };
}

/* ============================================
   URL PARSING
   ============================================ */

export function parseRefFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get('ref');
    return ref ? ref.toUpperCase().trim() : null;
  } catch (_) { return null; }
}

/* ============================================
   BANNER DISMISSAL (7-day localStorage)
   ============================================ */

const BANNER_DISMISS_KEY = 'shastraai_waitlist_banner_dismissed';
const BANNER_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

export function isBannerDismissed() {
  try {
    const ts = Number(localStorage.getItem(BANNER_DISMISS_KEY));
    if (!ts) return false;
    return Date.now() - ts < BANNER_DISMISS_MS;
  } catch (_) { return false; }
}

export function dismissBanner() {
  try { localStorage.setItem(BANNER_DISMISS_KEY, String(Date.now())); } catch (_) { /* skip */ }
}

/* ============================================
   FORMATTING
   ============================================ */

export function formatCount(n) {
  const num = Number(n) || 0;
  if (num >= 1000) return num.toLocaleString();
  return String(num);
}

export function formatBannerCount(total) {
  if (total < BANNER_COUNT_THRESHOLD) return 'Be one of the first';
  return `Join ${formatCount(total)} thinkers waiting`;
}

export function formatHeroCount(total) {
  if (total === 0) return 'Be the first on the list';
  if (total === 1) return '1 thinker is already on the list';
  if (total < BANNER_COUNT_THRESHOLD) return `${total} thinkers already on the list`;
  return `Joining ${formatCount(total)} others — already on the list`;
}
